import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Sparkles, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { GoogleMark } from "@/components/GoogleMark";
import { LOGIN } from "@/constants/testIds";
import { useSheetHistory } from "@/hooks/useSheetHistory";

const MIN_PASSWORD = 8; // mirrors the server's own minimum

// The backend sends these back as ?reason= when a Google round-trip fails.
const GOOGLE_REASONS = {
  oauth_failed: "errors.auth.googleFailed",
  no_verified_email: "errors.auth.googleNoEmail",
};

/**
 * What to put under the form when sign-in fails.
 *
 * The server answers 422 (pydantic) before it ever checks the password, so a
 * password below the minimum never reaches the credential check. On sign-in
 * that is reported as the same vague line as a wrong password on purpose —
 * the form must not hint at which half of the pair was wrong.
 */
function messageForError(t, error, mode) {
  const status = error?.status;
  // A 422 `detail` is pydantic's list of field errors, not a sentence.
  const detail = error?.detail;
  if (status === 422) {
    const field = Array.isArray(detail) ? detail[0]?.loc?.slice(-1)[0] : null;
    if (field === "email") return t("errors.auth.invalidEmail");
    if (mode === "register") return t("errors.auth.passwordTooShort", { count: MIN_PASSWORD });
    return t("errors.auth.wrongCredentials");
  }
  // A string `detail` is the server explaining itself — an account lockout, for
  // one — and it is kept rather than flattened into a generic apology.
  if (typeof detail === "string" && detail) return detail;
  if (status === undefined) return t("errors.network");
  return t("errors.generic");
}

/**
 * Sign in / create a profile, as a bottom sheet over whatever screen asked for
 * it — a guest can be mid-order, and sending them to a separate page would
 * throw the cart away.
 */
const AuthSheet = ({ initialMode = "signin", onClose, onSuccess }) => {
  useSheetHistory(true, onClose);
  const { providers, authError, loginWithPassword, register, loginWithGoogle } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  // A failed Google round-trip has no form of its own to report into; it lands
  // back on the screen the guest left, so show it here when they try again.
  const [error, setError] = useState(
    authError ? t(GOOGLE_REASONS[authError] || "errors.auth.signInFailed") : null
  );
  const emailRef = useRef(null);

  const isRegister = mode === "register";

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // Escape closes, like every other dismissible layer in the app.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    if (isRegister && password.length < MIN_PASSWORD) {
      setError(t("errors.auth.passwordTooShort", { count: MIN_PASSWORD }));
      return;
    }
    setPending(true);
    setError(null);
    try {
      if (isRegister) await register(email, password, name);
      else await loginWithPassword(email, password);
      onSuccess?.();
    } catch (err) {
      setError(messageForError(t, err, mode));
      setPassword("");
      setPending(false);
    }
  };

  return (
    <div
      data-testid={LOGIN.sheet}
      // amp-block: an email address and a password are typed here. Session
      // replay masks text everywhere; this sheet is not recorded at all.
      className="fixed inset-0 z-[95] bg-black/50 flex items-end sm:items-center justify-center animate-in fade-in duration-200 amp-block"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto bg-[#F9F9F7] rounded-t-[28px] sm:rounded-3xl p-6 pb-8 relative animate-in slide-in-from-bottom-8 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid={LOGIN.close}
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center active:scale-95 transition-transform"
          aria-label={t("common.close")}
        >
          <X size={18} />
        </button>

        <div className="w-11 h-11 rounded-full bg-[#E34A27]/10 flex items-center justify-center mb-3">
          <Sparkles size={20} className="text-[#E34A27]" />
        </div>
        <h3 className="text-2xl font-black tracking-tight text-neutral-900 pr-8 leading-tight">
          {isRegister ? t("auth.registerTitle") : t("auth.signInTitle")}
        </h3>
        <p className="text-sm text-neutral-600 mt-1.5">
          {isRegister ? t("auth.registerSubtitle") : t("auth.signInSubtitle")}
        </p>

        <div className="mt-5 grid grid-cols-2 gap-1 p-1 rounded-2xl bg-neutral-200/60">
          <ModeTab
            testid={LOGIN.modeSignIn}
            active={!isRegister}
            onClick={() => switchMode("signin")}
            label={t("auth.signIn")}
          />
          <ModeTab
            testid={LOGIN.modeRegister}
            active={isRegister}
            onClick={() => switchMode("register")}
            label={t("auth.createAccount")}
          />
        </div>

        <form data-testid={LOGIN.form} onSubmit={submit} className="mt-5 space-y-3">
          {isRegister && (
            <Field label={t("common.name")}>
              <input
                data-testid={LOGIN.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("common.optional")}
                autoComplete="name"
                disabled={pending}
                className={INPUT_CLASS}
              />
            </Field>
          )}

          <Field label={t("auth.email")}>
            <input
              ref={emailRef}
              data-testid={LOGIN.emailInput}
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
              required
              disabled={pending}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              className={INPUT_CLASS}
            />
          </Field>

          <Field label={t("auth.password")}>
            <div className="relative">
              <input
                data-testid={LOGIN.passwordInput}
                type={showPassword ? "text" : "password"}
                autoComplete={isRegister ? "new-password" : "current-password"}
                required
                disabled={pending}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={
                  isRegister ? t("auth.passwordPlaceholder", { count: MIN_PASSWORD }) : "••••••••"
                }
                className={`${INPUT_CLASS} pr-12`}
              />
              <button
                type="button"
                data-testid={LOGIN.passwordToggle}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl flex items-center justify-center text-neutral-500 active:scale-95 transition-transform"
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </Field>

          {error && (
            <div
              data-testid={LOGIN.error}
              role="alert"
              className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid={LOGIN.submitButton}
            type="submit"
            disabled={pending || !email || !password}
            className="w-full min-h-14 py-2 px-4 rounded-2xl bg-[#E34A27] text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {pending
              ? isRegister
                ? t("auth.creating")
                : t("auth.signingIn")
              : isRegister
              ? t("auth.createProfile")
              : t("auth.signIn")}
          </button>
        </form>

        {providers.google && (
          <>
            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-neutral-200" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                {t("auth.or")}
              </span>
              <span className="h-px flex-1 bg-neutral-200" />
            </div>
            {/* The mark is what makes this read as Google's button rather than
                as one more of ours — a guest is about to hand over their Google
                account, and the row before that should look like it. */}
            <button
              data-testid={LOGIN.googleButton}
              onClick={loginWithGoogle}
              className="w-full min-h-14 py-2 px-4 rounded-2xl bg-white border border-neutral-200 text-neutral-800 font-bold text-sm shadow-sm flex items-center justify-center gap-3 active:scale-[0.98] active:bg-neutral-50 transition-all"
            >
              <GoogleMark size={19} className="shrink-0" />
              {t("auth.google")}
            </button>
          </>
        )}

        <p className="text-[11px] text-neutral-400 mt-4 text-center">
          {isRegister ? t("auth.footerRegister") : t("auth.footerSignIn")}
        </p>
      </div>
    </div>
  );
};

const INPUT_CLASS =
  "w-full h-14 bg-white border border-neutral-200 rounded-2xl px-4 text-base font-medium text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900 transition-colors disabled:opacity-60";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
      {label}
    </label>
    {children}
  </div>
);

const ModeTab = ({ testid, active, onClick, label }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`h-11 rounded-xl text-sm font-bold transition-colors ${
      active ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600"
    }`}
  >
    {label}
  </button>
);

export default AuthSheet;
